import { Checkbox, Collapse, Divider, Icon, Tooltip } from '@mui/material'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import NewButton from 'ui/atoms/NewButton/NewButton'
import SearchField from 'ui/atoms/SearchField/SearchField'
import { getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'

type FilterDropdownProps = {
  label: string
  options: string[]
  translations?: { [key: string]: string }
}

//A static filter dropdown to be used in Tour for filter component, only displays the symbols that are available in the component
export default function StaticFilterDropdown({ label, options, translations }: FilterDropdownProps): ReactElement {
  const { t, i18n } = useTranslation()

  const dropdownLabel = getPrettyName(label, translations)
  const selectedLanguage = i18n.language
  const dropdownRef = useRef<HTMLDivElement>(null)

  const searchValue = ''
  const selectAllIsChecked = false
  const selectAllSearchedIsChecked = false
  const [expanded, setExpanded] = useState<boolean>(false)

  const numberOfFiltersApplied = 3

  const translatedChildren = useMemo(() => {
    return new Map(options.map((child) => [child, getPrettyName(child, translations)]))
  }, [options, translations])

  const getTranslation = useCallback(
    (child: string): string => translatedChildren.get(child) ?? child,
    [translatedChildren]
  )

  // Handles click outside of the dropdown which should close it and apply the filters
  useEffect(() => {
    if (!expanded) return

    function handleClickOutsideDropdown(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setExpanded(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutsideDropdown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideDropdown)
    }
  }, [expanded])

  return (
    <>
      <div ref={dropdownRef} className={'relative'}>
        <div
          className={`flex flex-row items-center justify-between border-gray-300 w-62 h-10 border ${expanded ? 'rounded-t-md border-b-0' : 'rounded-md'} p-2 mt-2`}
          onClick={() => setExpanded(!expanded)}
        >
          <div className="mx-1 flex flex-row justify-between w-full">
            {dropdownLabel}
            <div className="flex gap-1">
              <Tooltip title={t('Group filter applied')} arrow placement="top">
                <span className="border border-vercel-primary rounded-md px-1">
                  {/* @ts-ignore: It states extra-small doesn't exists even when it does*/}
                  <Icon className="fas fa-layer-group" fontSize="extra-small" color="primary" />
                </span>
              </Tooltip>

              {numberOfFiltersApplied > 0 && (
                <Tooltip
                  title={t('{{numberOfFilters}} filters applied', { numberOfFilters: numberOfFiltersApplied })}
                  arrow
                  placement="top"
                >
                  <span
                    className={`border border-vercel-primary rounded-md ${numberOfFiltersApplied > 9 ? 'px-1' : 'px-1.5'} text-vercel-primary`}
                  >
                    {numberOfFiltersApplied}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
          {/* @ts-ignore: It states extra-small doesn't exists even when it does*/}
          <Icon fontSize="extra-small" className={expanded ? 'fal fa-chevron-up' : 'fal fa-chevron-down'} />
        </div>
        <Collapse in={expanded} timeout="auto">
          <div className="flex flex-col w-62 bg-white border border-gray-300 rounded-b-md border-t-0 p-2 absolute z-10">
            <SearchField value={searchValue} onChange={() => {}} onClear={() => {}} />
            <div className="flex flex-col">
              {
                <div
                  className="flex flex-row items-center justify-between bg-gray-200 rounded-md pl-1.5 mt-2 hover:cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  {t('Group filter')}
                  <Checkbox
                    checked
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                  />
                </div>
              }
              {searchValue === '' ? (
                <div
                  className="flex flex-row items-center justify-between ml-1 :hover:cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  {t('Select all')}
                  <Checkbox
                    checked={selectAllIsChecked}
                    onChange={(e) => {
                      e.stopPropagation()
                    }}
                  />
                </div>
              ) : (
                <div
                  className="flex flex-row items-center justify-between ml-1 hover:cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  {t('Select searched filters')}
                  <Checkbox
                    checked={selectAllSearchedIsChecked}
                    onChange={(e) => {
                      e.stopPropagation()
                    }}
                  />
                </div>
              )}

              <Divider />
              <div className="overflow-y-auto max-h-[45vh]">
                {options
                  .sort((a, b) => getTranslation(a).localeCompare(getTranslation(b), selectedLanguage))
                  .map((child, index) => (
                    <div
                      className="flex flex-row items-center justify-between ml-1 hover:cursor-pointer"
                      key={`${child}.${index}`}
                    >
                      {getPrettyName(child, translations)}
                      <Checkbox checked={false} />
                    </div>
                  ))}
              </div>
            </div>

            <Divider />

            <div className="flex flex-row items-centers justify-between mt-1">
              <NewButton variant="outlined" label={t('Reset filters')} onClick={(e) => e.stopPropagation()} />
              <NewButton label={t('Apply')} onClick={(e) => e.stopPropagation()} />
            </div>
          </div>
        </Collapse>
      </div>
    </>
  )
}
